import React from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from 'react-query';

import * as Sentry from '@sentry/react';
import { configure } from 'mobx';

import { Button, ConfigProvider } from 'antd';
import ptBR from 'antd/lib/locale/pt_BR';

import { queryClient } from './services/queryClient';

import { App } from './App';
import { PanelResult, PanelResultStatus } from './components/PanelResult';
import packageJson from '../package.json';

import './i18n';
import './utils/dayjs';
import { reloadForStaleChunk } from './utils/staleChunk';

// Styles
import 'antd/dist/reset.css'

import 'react-confirm-alert/src/react-confirm-alert.css';
import 'react-phone-number-input/style.css'
import 'react-loading-skeleton/dist/skeleton.css'

// Our bundle ships a single MobX, but some browser extensions inject their own
// into the page, and MobX then throws error 35 ("multiple MobX instances").
// Isolating our global state avoids sharing it with theirs.
configure({ isolateGlobalState: true });


Sentry.init({
  beforeSend(event) {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    const isThirdParty = frames.some((f) => f.filename?.includes('goadopt.io'));
    if (isThirdParty) return null;
    // In-app browsers (iOS WebKit, Instagram on Android) inject a script that
    // throws "evaluating 'e.config'" / "reading 'config'" (frame points at the HTML document or <anonymous> at a column
    // our index.html doesn't have). Not ours; only dropped when no frame is
    // from our bundle, so a real "x.config" bug in /assets/ still reports.
    const message = event.exception?.values?.[0]?.value ?? '';
    const isInjectedWebKitScript =
      /evaluating '\w+\.config'|reading 'config'/.test(message) && !frames.some((f) => f.filename?.includes('/assets/'));
    if (isInjectedWebKitScript) return null;
    // Axios "Network Error" = no response at all (offline, tab suspended on iOS). Not actionable;
    // the user sees the OfflineBanner on the Dynamic Menu instead.
    if (message === 'Network Error') return null;
    // Lazy chunk failed while offline (Safari / Chrome / Firefox wording). Online failures still
    // report, since those may mean a broken deploy.
    const isChunkLoadError = /Importing a module script failed|dynamically imported module/.test(message);
    if (isChunkLoadError && !navigator.onLine) return null;
    return event;
  },
  dsn: 'https://3049ca6fc1c64518a74331c293d97bdc@o863288.ingest.sentry.io/5822185',
  enabled: import.meta.env.VITE_APP_NODE_ENV !== 'development',

  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  release: packageJson.version,
  // The API has no Sentry, so trace headers only bought a CORS preflight on every call.
  tracePropagationTargets: [],
  tracesSampleRate: 0.2,
});

if (import.meta.env.VITE_APP_NODE_ENV !== 'development') {
  // iOS Safari can resolve a failed import() with undefined; replay is optional, so just skip it.
  import('@sentry/react')
    .then((m) => m?.replayIntegration && Sentry.addIntegration(m.replayIntegration()))
    .catch(() => {});
}

// A lazy chunk failed to load: reload once to fetch the fresh index.html (see reloadForStaleChunk).
// If it fails again, the error propagates to the ErrorBoundary as before.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForStaleChunk()) event.preventDefault();
});

const root = createRoot(document.getElementById('root')!);

root.render(
  <React.StrictMode>
    <ConfigProvider
      locale={ptBR}
      theme={{
        components: {
          Button: {
            borderRadius: 10,
            controlHeight: 45,
            fontWeight: 'bold',
          },
        },
        token: {
          colorPrimary: '#31B86F',
          // antd v5+ no longer derives colorInfo from colorPrimary
          colorInfo: '#31B86F',
        },
      }}
    >
      <Toaster />
      {/* Without this, an uncaught render error blanks the whole page (React
          unmounts the tree) — Sentry still gets it via the default global
          handlers, but the visitor sees nothing. A full reload is the
          recovery here rather than resetError(): a bug that crashed the
          render will very likely crash it again on the same state. */}
      <Sentry.ErrorBoundary
        fallback={() => (
          <PanelResult
            status={PanelResultStatus.ERROR}
            title="Ocorreu um erro inesperado"
            subtitle="Por favor, recarregue a página. Se o problema continuar, entre em contato com o suporte."
            extra={<Button type="primary" onClick={() => window.location.reload()}>Recarregar página</Button>}
          />
        )}
      >
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </ConfigProvider>
  </React.StrictMode>
);